import React from "react";
import { Box, Skeleton, useTheme } from "@mui/material";

/**
 * Deliberately minimal (Ben, 2026-08-01): no outlines for tabs, range chips
 * or other button controls — just the header text and the outlined cards
 * that hold the charts, each with a single pulsing fill. Default MUI pulse
 * and theme colours, matching every other skeleton in the app.
 */
export const ViewTickerMobileSkeleton: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const flatUIColors = {
    background: isDark ? "#1a1a1a" : "#ffffff",
    border: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0",
  };

  // Mobile cards use the raw value (matches the real mobile body cards in
  // ViewTicker.tsx renderOverviewTab) rather than flatUIColors.surface.
  const cardSurface = isDark ? "#212121" : "#f5f5f5";

  const cardSx = {
    backgroundColor: cardSurface,
    border: `1px solid ${flatUIColors.border}`,
    borderRadius: 2,
    p: 1.5,
    width: "100%",
    boxSizing: "border-box" as const,
    overflow: "hidden",
  };

  // MUI Skeleton's root defaults to a 1.2em height, so full-card fills need
  // the explicit override to stretch inside their flex card.
  const fillSx = { flex: 1, height: "auto", borderRadius: 1 };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        // Mobile: natural page scroll. Matches ViewTicker's outer container.
        height: "auto",
        minHeight: "100vh",
        backgroundColor: flatUIColors.background,
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden",
        // MUI's default pulse waits 0.5s before its first cycle and fades
        // over 2s — on a fast load that reads as a static grey block. Faster
        // cycle, no delay, deeper dip so the pulse is unmistakable.
        "@keyframes tickerSkeletonPulse": {
          "0%": { opacity: 1 },
          "50%": { opacity: 0.35 },
          "100%": { opacity: 1 },
        },
        "& .MuiSkeleton-root": {
          animation: "tickerSkeletonPulse 1.1s ease-in-out infinite",
        },
      }}
    >
      {/* Compact Header — mirrors CompactHeader's mobile branch. Not sticky. */}
      <Box
        sx={{
          minHeight: 56,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          borderBottom: `1px solid ${flatUIColors.border}`,
          backgroundColor: flatUIColors.background,
        }}
      >
        {/* Left: Logo + Symbol/Exchange */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: 1 }} />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
            <Skeleton variant="text" width={60} height={16} />
            <Skeleton variant="text" width={40} height={12} />
          </Box>
        </Box>

        {/* Right: Price/Change */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 0.25,
          }}
        >
          <Skeleton variant="text" width={60} height={18} />
          <Skeleton variant="text" width={45} height={14} />
        </Box>
      </Box>

      {/* Body — outlined chart cards only, matching the real card stack */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          px: 2,
          pt: 2,
          pb: 3,
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        {/* Compact Profile Card */}
        <Box sx={{ ...cardSx, height: 96, display: "flex" }}>
          <Skeleton variant="rounded" sx={fillSx} />
        </Box>

        {/* Price Chart Card */}
        <Box sx={{ ...cardSx, height: 280, display: "flex" }}>
          <Skeleton variant="rounded" sx={fillSx} />
        </Box>

        {/* Volume Chart Card */}
        <Box sx={{ ...cardSx, height: 240, display: "flex" }}>
          <Skeleton variant="rounded" sx={fillSx} />
        </Box>

        {/* Key Statistics Card */}
        <Box sx={{ ...cardSx, height: 134, display: "flex" }}>
          <Skeleton variant="rounded" sx={fillSx} />
        </Box>

        {/* Market Cap Chart Card */}
        <Box sx={{ ...cardSx, height: 250, display: "flex" }}>
          <Skeleton variant="rounded" sx={fillSx} />
        </Box>
      </Box>
    </Box>
  );
};
